"use client";

import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { de, type Locale, type TranslationKey } from "./translations";

type TranslationMap = Record<TranslationKey, string>;

interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey) => string;
}

const I18nContext = createContext<I18nContextType | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>("de");
  const [messages, setMessages] = useState<TranslationMap>(de);

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    if (newLocale === "en") {
      import("./translations/en").then((mod) => setMessages(mod.en as TranslationMap));
    } else {
      setMessages(de);
    }
  }, []);

  const t = useCallback(
    (key: TranslationKey): string => {
      return messages[key] ?? key;
    },
    [messages]
  );

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useTranslation must be used within an I18nProvider");
  }
  return context;
}

export type { Locale, TranslationKey };
